/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table/Table';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {CarbDevices, Devices, EntityStatus} from '@ion/core/src/types/entities';
import {DATE_RANGE_FORMAT, dateFormatter, isValid, resolveDateInput} from '@ion/core/src/utils/DateUtil';
import Alert from '@ion/dls/src/components/alert/Alert';
import CTALink from '@ion/dls/src/components/buttons/CTALink';
import {IconButton as DeleteIconButton} from '@ion/dls/src/components/buttons/IconButton';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {DatePicker} from '@ion/dls/src/editors/datetimepicker/DatePicker';
import DeletePrompt from '@ion/dls/src/editors/DeletePrompt';
import {TargetType} from '@ion/dls/src/editors/DeviceEditorUtils';
import {DeleteIcon} from '@ion/dls/src/icons/DeleteIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {toLocalRenewalDate, VIN_REG_EXP} from '../BulkSelectEntitiesEditorUtils';

export interface CarbDevicesTableProps {
  setIsProcessButtonDisabled?: Function;
  selectedIdentifierType?: string;
  selectedRowEntities: Devices;
  isSelectedFromGrid?: boolean;
  setCarbDevices: Function;
  serviceStatus?: EntityStatus;
  devices: CarbDevices;
  devicesRef?: any;
  error?: boolean;
};

const useStyles = makeStyles(() => ({
  value : {
    fontFamily : 'Roboto',
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  },
  paginationContainer : {
    backgroundColor : dlsColor('white'),
    outline         : `1px solid ${dlsColor('grey5')}`,
    width           : '100%',
    display         : 'flex',
    justifyContent  : 'end',
    alignItems      : 'center',
    marginTop       : '10px'
  },
  deviceTableContainer : {
    paddingTop    : 15,
    paddingBottom : 30
  },
  deviceTable : {
    backgroundColor : dlsColor('white')
  },
  headers : {
    display        : 'flex',
    justifyContent : 'space-between',
    fontSize       : '14px',
    fontWeight     : 500,
    paddingBottom  : 10,
    color          : dlsColor('grey2'),
    textTransform  : 'uppercase',
    alignItems     : 'center'
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  primaryCell : {
    borderTop     : `2px solid ${dlsColor('grey5')}`,
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    lineHeight    : '16px',
    padding       : '4px 12px',
    letterSpacing : '1px',
    maxHeight     : '48px'
  },
  deleteCell : {
    width        : '5%',
    padding      : 0,
    borderBottom : `1px solid ${dlsColor('grey5')}`,
    borderLeft   : `1px solid ${dlsColor('grey5')}`
  },
  secondaryCell : {
    width        : '45%',
    padding      : 0,
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  tableHeader : {
    display    : 'flex',
    alignItems : 'center'
  },
  syncStatus : {
    color : dlsColor('aaBlue')
  },
  secondaryheader : {
    paddingLeft : 20
  },
  noData : {
    ...dlsTypography('body1'),
    alignItems     : 'center',
    textAlign      : 'center',
    fontSize       : 16,
    height         : 50,
    display        : 'flex',
    justifyContent : 'center'
  },
  headerBorder : {
    borderLeft  : `1px solid ${dlsColor('grey3')}`,
    paddingLeft : 12

  },
  headerCell : {
    color           : dlsColor('grey1'),
    borderTop       : `1px solid ${dlsColor('grey5')}`,
    backgroundColor : dlsColor('white'),
    fontSize        : '12px',
    lineHeight      : '12px',
    letterSpacing   : '1px',
    textTransform   : 'uppercase',
    fontWeight      : 600,
    minHeight       : '40px',
    borderBottom    : `1px solid ${dlsColor('grey5')}`
  },
  actionHeader : {
    width : '5%'
  },
  disabled : {
    fontSize   : '16px',
    fontWeight : 500,
    color      : dlsColor('grey3')
  },
  ctaLink : {
    ...dlsTypography('cta'),
    color         : dlsColor('caBlue'),
    cursor        : 'pointer',
    textTransform : 'uppercase',
    letterSpacing : '1px',
    lineHeight    : 'inherit',
    fontSize      : 'inherit',
    display       : 'block',
    alignItems    : 'center',
    '&&:hover'    : {textDecoration: 'underline'}
  }
}));

const useTooltipStyles = makeStyles(theme => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip'),
    fontSize : '12px !important'
  }
}));

const ITEMS_PER_PAGE = 20;

enum CarbDeviceFields {
  VIN = 'vin',
  LICENSE_PLATE = 'licensePlate',
  LICENSE_STATE = 'licenseState',
};

const columns = {
  vin                     : 'VIN',
  licensePlate            : 'License Plate',
  licenseState            : 'License State',
  registrationRenewalDate : 'Registration Renewal Date',
  action                  : ''
};

const deserialize = (devices) => {
  return {
    devices : devices?.map((device) => {
      const {vin, identifier, licensePlate, licenseState, registrationRenewalDate} = device || {};
      return {
        identifier : {
          value : identifier
        },
        vin : {
          value    : vin,
          hasError : !VIN_REG_EXP.test(vin) || !vin
        },
        licensePlate : {
          value    : licensePlate,
          hasError : !licensePlate
        },
        licenseState : {
          value    : licenseState,
          hasError : !licenseState
        },
        registrationRenewalDate : {
          value    : registrationRenewalDate,
          hasError : !registrationRenewalDate
        }
      };
    })
  };
};

const fields = ['licensePlate', 'licenseState'];

export const CarbDevicesTable = ({
  devices,
  devicesRef,
  serviceStatus,
  selectedRowEntities,
  isSelectedFromGrid,
  selectedIdentifierType,
  setIsProcessButtonDisabled
}: CarbDevicesTableProps) => {
  const classes                             = useStyles();
  const tooltipClasses                      = useTooltipStyles();
  const [currentPage, setCurrentPage]       = useState(1);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [showPrompt, setShowPrompt]         = useState(false);
  const totalPages                          = Math.ceil(devices.length / ITEMS_PER_PAGE);
  const startItem                           = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem                             = Math.min(currentPage * ITEMS_PER_PAGE, devices.length);
  const [formState, setFormState]           = useState(deserialize(devices));

  const rowRefs                                   = useRef({});
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);

  const errorRows = useMemo(() => {
    return formState?.devices
      ?.map((device, index) => ({
        index,
        identifier : device.identifier?.value,
        hasError   : Object.values(device).some(field => field?.hasError)
      }))
      .filter(row => row.hasError);
  }, [formState]);

  useEffect(() => {
    const isProcessButtonDisabled = formState?.devices?.some(device =>
      Object.values(device)?.some(field => field?.hasError)
    );
    setIsProcessButtonDisabled?.(!!isProcessButtonDisabled);
  }, [formState]);

  useEffect(() => {
    setFormState(deserialize(devices));
  }, [devices]);

  useEffect(() => {
    setCurrentPage(1);
  }, [devices]);

  const onNextError = function() {
    const next = (currentErrorIndex + 1) % errorRows.length;
    setCurrentErrorIndex(next);
    jumpToError(next);
  };

  const jumpToError = useCallback((errorPosition) => {
    const errorRow = errorRows?.[errorPosition];

    if (errorRow) {
      const page = Math.floor(errorRow.index / ITEMS_PER_PAGE) + 1;
      setCurrentPage(page);

      requestAnimationFrame(() => {
        const rowEl =
      rowRefs.current[errorRow.identifier];

        rowEl?.scrollIntoView({
          behavior : 'smooth',
          block    : 'center'
        });
      });
    }
  }, [errorRows]);

  const overrideDeleteMesssage = useCallback(function() {
    return translate('common:editors.bulkCarb.deleteMessage');
  }, [selectedDevice]);

  const currentDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return formState?.devices?.slice(startIndex, endIndex);
  }, [currentPage, formState, selectedDevice]);
    
  const handlePreviousPage = useCallback(function() {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);
    
  const handleNextPage = useCallback(function() {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const onDeleteCancel = function() {
    setShowPrompt(false);
    setSelectedDevice({});
  };

  const handleDelete = function({device}) {
    const {identifier} = device;
    const updatedDevs = devicesRef?.current?.map(pairedDevice =>
      pairedDevice.identifier === identifier.value
        ? {...pairedDevice, licensePlate: '', licenseState: ''}
        : pairedDevice
    );
    devicesRef.current = updatedDevs;
    setFormState(prev => {
      let updated = prev;

      fields.forEach((field) => {
        updated = updateFormState(updated, {
          identifier : identifier.value,
          field,
          value      : ''
        });
      });

      return updated;
    });
  };
  
  const updateFormState = (prevState, {
    identifier,
    field,
    value
  }) => {
    return {
      devices : prevState?.devices?.map((row) => {
        if (row?.identifier?.value !== identifier) {
          return row;
        }
        const currentCell = row[field];

        const hasError = field === 'vin'
          ? !VIN_REG_EXP.test(value)
          : value === '';

        return {
          ...row,
          [field] : {
            ...currentCell,
            value,
            hasError
          }
        };
      })
    };
  };

  const debounceFieldChange = (target: TargetType, id) => {
    const {name, value} = target;
    const newValue = value.trim();
    const newDevs = devicesRef?.current.map(device =>
      device?.identifier === id
        ? {...device, [name]: newValue}
        : device
    );
    devicesRef.current = newDevs;
    setFormState(prev => updateFormState(prev, {identifier: id, field: name, value: newValue}));
  };
  
  const handleFieldChange = (id) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      e.persist();
      debounceFieldChange(e?.target, id);
    };
  };

  const onRenewalDateChange = (value, id) => {
    const newDate = isValid(value) ? dateFormatter(resolveDateInput(value), DATE_RANGE_FORMAT) : '';
    const newDevs = devicesRef?.current?.map(device =>
      device?.identifier === id
        ? {...device, registrationRenewalDate: newDate}
        : device
    ) ?? [];
    devicesRef.current = newDevs;
    setFormState(prev => updateFormState(prev, {identifier: id, field: 'registrationRenewalDate', value: newDate}));
  };
  
  const onDeleteConfirm = useCallback(function() {
    setShowPrompt(false);
    handleDelete(selectedDevice);
  }, [selectedDevice]);
  
  const onDeleteClick = function(selectedDevice) {
    return () => {
      setShowPrompt(true);
      setSelectedDevice(selectedDevice);
    };
  };

  const getIdentifier = (id) => {
    if (isSelectedFromGrid && selectedRowEntities?.length) {
      return selectedRowEntities?.find(device => String(device?.id) === String(id))?.esn || id;
    }
    return id;
  };

  const carbColumns = {
    [selectedIdentifierType] : isSelectedFromGrid ? 'ESN' : selectedIdentifierType?.toUpperCase(),
    ...columns
  };
  
  return (<div style={{paddingBottom: 20}}>
    {errorRows.length > 0 && (
      <Alert type='severe'
        gutters={false}
        action={
          <CTALink className={classes.ctaLink} onClick={onNextError}>
            {translate('common:jumpToNextError')}
          </CTALink>}
        message={translate('common:alertMsg', {count: errorRows?.length})} />
    )}
    <Table>
      <TableHead>
        <TableRow>
          {Object.keys(carbColumns).map((label, index) => (
            <TableCell key={`title-${index}`} align='left'
              className={classnames(classes.headerCell)}>
              <div className={index !== 0 ? classes.headerBorder : null}>
                {carbColumns[label]}</div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody className={classes.deviceTable}>
        {currentDevices?.length ? (currentDevices.map((device) => {
          return (
            <TableRow key={`row-${device?.identifier?.value}`}
              ref={(el) => { rowRefs.current[device.identifier?.value] = el; }}>
              <TableCell className={classnames(classes.primaryCell, classes.disabled)}>
                {getIdentifier(device?.identifier?.value)}
              </TableCell>
              <TableCell className={classes.primaryCell}>
                <FormTextField required
                  disabled={serviceStatus === EntityStatus.Disabled}
                  placeholder={translate('common:editors.vin.placeholder')}
                  name={CarbDeviceFields.VIN}
                  maxLength={64}
                  onChange={handleFieldChange(device.identifier?.value)}
                  hasError={device?.vin?.hasError}
                  errorText={translate('common:editors.vin.errorText')}
                  value={device?.vin?.value}/>
              </TableCell>
              <TableCell className={classes.primaryCell}>
                <FormTextField required
                  disabled={serviceStatus === EntityStatus.Disabled}
                  placeholder={translate('common:editors.licensePlate.placeholder')}
                  name={CarbDeviceFields.LICENSE_PLATE}
                  maxLength={64}
                  errorText={translate('common:editors.licensePlate.errorText')}
                  hasError={device?.licensePlate?.hasError}
                  onChange={handleFieldChange(device.identifier?.value)}
                  value={device?.licensePlate?.value}/>
              </TableCell>
              <TableCell className={classes.primaryCell}>
                <FormTextField required
                  disabled={serviceStatus === EntityStatus.Disabled}
                  placeholder={translate('common:editors.licenseState.placeholder')}
                  name={CarbDeviceFields.LICENSE_STATE}
                  maxLength={64}
                  errorText={translate('common:editors.licenseState.errorText')}
                  hasError={device?.licenseState?.hasError}
                  onChange={handleFieldChange(device.identifier?.value)}
                  value={device?.licenseState?.value}/>
              </TableCell>
              <TableCell className={classes.primaryCell}>
                <DatePicker closeOnClickOutside
                  useDatePickerTextField
                  hideClearButton
                  fieldLabel={''}
                  customLabel={false}
                  hasError={device?.registrationRenewalDate?.hasError}
                  errorText={translate('common:editors.registrationRenewalDate.errorText')}
                  defaultValue={toLocalRenewalDate(device?.registrationRenewalDate?.value)}
                  onChange={function(value) {
                    return onRenewalDateChange(value, device.identifier?.value);
                  }}
                  placeholder={translate('common:editors.registrationRenewalDate.placeholder')}/>
              </TableCell>
              <TableCell className={classes.deleteCell}>
                <Tooltip title={<span>{translate('common:carbService.carbDelete')}</span>}
                  placement={'bottom'}
                  classes={tooltipClasses}
                  arrow>
                  <DeleteIconButton disabled={!device?.licensePlate?.value && !device?.licenseState?.value}
                    icon={<DeleteIcon size={IconSize.medium} />}
                    onClick={onDeleteClick({device})}/>
                </Tooltip>
              </TableCell>
            </TableRow>);
        })) : <TableRow>
          <TableCell>
            <div className={classes.noData}>{translate('common:editors.bulkCarb.noDataMsg')}</div>
          </TableCell>
        </TableRow>}
      </TableBody>
    </Table>
    <Render.If condition={devices?.length > ITEMS_PER_PAGE}>
      <div className={classes.paginationContainer}>
        <span className={classes.value}>
          {translate('jobcreate:conflict.table.pagination.rowsPerPage', {from: startItem, to: endItem, count: devices.length})}
        </span>
        <div>
          <IconButton onClick={handlePreviousPage} disabled={currentPage === 1}>
            <ChevronLeft />
          </IconButton>
          <span className={classes.value}>
            {currentPage} / {totalPages}
          </span>
          <IconButton onClick={handleNextPage} disabled={currentPage === totalPages}>
            <ChevronRight />
          </IconButton>
        </div>
      </div>
    </Render.If>
    <DeletePrompt singleDeleteMessage
      items={[selectedDevice]}
      show={showPrompt}
      overrideMessage={overrideDeleteMesssage}
      entity={translate('common:editors.bulkCarb.assetDetails')}
      onConfirm={onDeleteConfirm}
      onCancel={onDeleteCancel} />
  </div>
  );
};

export default CarbDevicesTable;
